import { createElement, useEffect, useRef, useState } from "react";
import { Button } from "@component/button";
import { Label } from "@component/label";
import {
    PlusIcon,
} from "lucide-react";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@component/select";
import { useSnackbar } from "@hook/use-snackbar";
import { EmailTemplate, EmailTemplateRequest } from "@type/setting";
import { defaultAssessmentInvitationTemplate, defaultAssessmentReminderTemplate, defaultAssessmentRetakeTemplate, defaultHireTemplate, defaultInterviewInvitationTemplate, defaultJobInvitationTemplate, defaultRejectTemplate, templateCategories } from "@constant/email";
import { getCategoryColor, getCategoryIcon } from "@util/string";
import EmailVariablesPanel from "@container/email-variable-panel";
import TemplateDeleteDialog from "@container/template-delete-dialog";
import TemplateListItem from "@container/template-list-item";
import EmailTemplateCreateForm from "./email-template-create-form";
import EmailTemplateEditForm from "./email-template-edit-form";
import { createEmailTemplate, deleteEmailTemplate, getEmailTemplates, updateEmailTemplate, upsertDefaultEmailTemplate } from "@service/employer";
import { catchErrorLog } from "@util/sentry";
import { TextEditorRef } from "@component/rich-editor/v2/text-editor";
import { cn } from "@util/tailwind";
import { useUserProfileContext } from "@context/user_profile";
import { Input } from "@component/input";
import { updateCompanyMetadata, updateCompanyNotificationPreferences } from "@service/user";
import { Company, NotificationPreference } from "@type/company";
import { Checkbox } from "@component/checkbox";

interface Props {
    company: Company;
}

export default function EmailCommunicationSection(props: Props) {
    const snackbar = useSnackbar();
    const params = new URLSearchParams(window.location.search);
    const [, userInfo] = useUserProfileContext();
    const [senderName, setSenderName] = useState("ourteam");
    const [defaultReplyTo, setDefaultReplyTo] = useState("work@ourteam.ai");
    const [templates, setTemplates] = useState<EmailTemplate[]>([]);
    const [selectedCategory, setSelectedCategory] = useState<string>("reject");
    const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(null)
    const [isViewMode, setIsViewMode] = useState<boolean>(true);
    const [showVariables, setShowVariables] = useState<boolean>(false);
    const [isCreatingNew, setIsCreatingNew] = useState<boolean>(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
    const [templateToDelete, setTemplateToDelete] = useState<EmailTemplate | null>(null);
    const [lastFocusedField, setLastFocusedField] = useState<"subject" | "content">("content");
    const [allowJobBasedIdentity, setAllowJobBasedIdentity] = useState<boolean>(false);
    const [preferences, setPreferences] = useState<NotificationPreference[]>([
        {
            id: "candidate-activity",
            title: "Candidate Activity",
            description:
                "Get notified about meaningful candidate progress on jobs you’re involved in, such as when candidates are ready for review.",
            enabled: props.company?.preferences?.notify_candidate_activity || false,
        },
        {
            id: "hiring-workflow",
            title: "Hiring Workflow Updates",
            description: "Confirm key hiring actions on a job, including status changes, offers, reviews, or hires.",
            enabled: props.company?.preferences?.notify_hiring_workflow_update || false,
        },
        {
            id: "team-workspace",
            title: "Team & Workspace Updates",
            description: "Stay informed about workspace access and team membership changes, such as invitations and new members.",
            enabled: props.company?.preferences?.notify_team_workspace_update || false,
        },
        {
            id: "insights-reminders",
            title: "Insights & Reminders",
            description: "Receive high-level insights to monitor hiring performance for the roles you’re involved in.",
            enabled: props.company?.preferences?.notify_insight_reminders || false,
        },
    ]);

    const subjectInputRef = useRef<HTMLInputElement>(null);
    const contentEditorRef = useRef<TextEditorRef>(null);

    const isLaptopScreen = window.matchMedia("(max-height: 900px)").matches;
    const panelHeight = isCreatingNew ? "auto" : isLaptopScreen ? "calc(100vh - 200px)" : "calc(100vh - 300px)";

    useEffect(() => {
        const template = params.get("template");
        setSelectedCategory(template || "reject");
    }, [params.get("template")])

    const reloadTemplates = () => {
        getEmailTemplates().then((resp) => {
            if (resp.status === 200) {
                const beTemplates: EmailTemplate[] = resp.data.data || [];
                const beDefaultsMap = new Map(
                    beTemplates
                        .filter((t) => t.is_default && t.template_id)
                        .map((t) => [t.template_id as string, t])
                );

                const hardcodedDefaults: EmailTemplate[] = [
                    {
                        template_id: "default_reject",
                        template_name: "Standard Rejection Email",
                        template_category: "reject",
                        email_subject: defaultRejectTemplate(props.company).subject,
                        email_content: defaultRejectTemplate(props.company).content,
                        is_default: true,
                    },
                    {
                        template_id: "default_hire",
                        template_name: "Standard Job Offer Email",
                        template_category: "hire",
                        email_subject: defaultHireTemplate(props.company).subject,
                        email_content: defaultHireTemplate(props.company).content,
                        is_default: true,
                    },
                    {
                        template_id: "default_interview_invitation",
                        template_name: "Standard Review Invitation Email",
                        template_category: "interview-invitation",
                        email_subject: defaultInterviewInvitationTemplate(props.company).subject,
                        email_content: defaultInterviewInvitationTemplate(props.company).content,
                        is_default: true,
                    },
                    {
                        template_id: "default_job_invitation",
                        template_name: "Standard Job Invitation Email",
                        template_category: "job-invitation",
                        email_subject: defaultJobInvitationTemplate(props.company).subject,
                        email_content: defaultJobInvitationTemplate(props.company).content,
                        is_default: true,
                    },
                    {
                        template_id: "default_assessment_invitation",
                        template_name: "Standard Screening Invitation Email",
                        template_category: "assessment-invitation",
                        email_subject: defaultAssessmentInvitationTemplate(7).subject,
                        email_content: defaultAssessmentInvitationTemplate(7).content,
                        is_default: true,
                    },
                    {
                        template_id: "default_assessment_retake",
                        template_name: "Standard Interview Request Retake Email",
                        template_category: "interview-invitation",
                        email_subject: defaultAssessmentRetakeTemplate().subject,
                        email_content: defaultAssessmentRetakeTemplate().content,
                        is_default: true,
                    },
                    {
                        template_id: "default_assessment_reminder",
                        template_name: "Standard Screening Reminder Email",
                        template_category: "assessment-invitation",
                        email_subject: defaultAssessmentReminderTemplate().subject,
                        email_content: defaultAssessmentReminderTemplate().content,
                        is_default: true,
                    },
                ];

                const mergedDefaults = hardcodedDefaults.map((hardcoded) =>
                    beDefaultsMap.get(hardcoded.template_id as string) ?? hardcoded
                );

                const customTemplates = beTemplates.filter((t) => !t.is_default);

                setTemplates([...mergedDefaults, ...customTemplates]);
            }
        })
    }

    useEffect(() => {
        reloadTemplates()
    }, [])

    useEffect(() => {
        if (props.company) {
            setSenderName(props.company.company_metadata?.outgoing_email?.sender_name || "ourteam");
            setDefaultReplyTo(props.company.company_metadata?.outgoing_email?.default_reply_to || "work@ourteam.ai");
            setAllowJobBasedIdentity(props.company.company_metadata?.outgoing_email?.is_job_specific_outgoing_email || false);
        }
    }, [props.company])

    useEffect(() => {
        if (selectedCategory && templates.length > 0) {
            const defaultTemplate = templates.find(t => t.template_category === selectedCategory && t.is_default);
            if (defaultTemplate) {
                setSelectedTemplate(defaultTemplate);
                setIsViewMode(true);
                setIsCreatingNew(false);
            } else {
                setSelectedTemplate(null);
            }
        }
    }, [selectedCategory, templates])

    const handleDeleteTemplate = (template: EmailTemplate) => {
        // Prevent deletion of default templates
        if (template.is_default) {
            return;
        }
        setTemplateToDelete(template);
        setDeleteDialogOpen(true);
    };

    const handleConfirmDelete = (template: EmailTemplate) => {
        // Prevent deletion of default templates
        if (template.is_default) {
            return;
        }

        deleteEmailTemplate(template.template_id as string).then((resp) => {
            if (resp.status === 200) {
                snackbar.showSuccess("Template deleted successfully.");
                reloadTemplates()
            }
        }).catch((error) => {
            snackbar.showError("Failed to delete template. Please try again.");
            catchErrorLog(`failed to delete email template: ${error}`);
        }).finally(() => {
            setDeleteDialogOpen(false);
            setTemplateToDelete(null);
            setSelectedTemplate(null);
            setIsViewMode(true);
        });
    };

    const handleCategoryChange = (category: string) => {
        setSelectedCategory(category);
        setSelectedTemplate(null);
    }

    const handleCreateNewTemplate = () => {
        setSelectedTemplate(null);
        setIsCreatingNew(true);
    };

    const handleSaveTemplate = (data: EmailTemplateRequest) => {
        if (selectedTemplate) {
            const savePromise = selectedTemplate.is_default
                ? upsertDefaultEmailTemplate(selectedTemplate.template_id as string, data)
                : updateEmailTemplate(selectedTemplate.template_id as string, data);

            savePromise.then((resp) => {
                if (resp.status === 200) {
                    snackbar.showSuccess("Template updated successfully.");
                    setSelectedTemplate((prev) => prev ? { ...prev, ...data } : null);
                }
            }).catch((error) => {
                snackbar.showError("Failed to update template. Please try again.");
                catchErrorLog(`failed to update email template: ${error}`);
            }).finally(() => {
                setIsCreatingNew(false);
                setIsViewMode(true);
                reloadTemplates()
            })
        } else {
            setSelectedTemplate(null);
            createEmailTemplate(data).then((resp) => {
                if (resp.status === 200) {
                    const newTemplate: EmailTemplate = {
                        template_id: resp.data.template_id,
                        ...data,
                        is_default: false,
                        updated_at: new Date().toISOString(),
                    }
                    setSelectedTemplate(newTemplate);
                    snackbar.showSuccess("New template created successfully.");
                }
            }).catch((error) => {
                snackbar.showError("Failed to create template. Please try again.");
                catchErrorLog(`failed to create email template: ${error}`);
            }).finally(() => {
                setIsCreatingNew(false);
                setIsViewMode(true);
                reloadTemplates()
            })
        }
    };

    const handleCancelNewTemplate = () => {
        setIsCreatingNew(false);
    };

    const handleInsertVariable = (variable: string) => {
        // Get references to both fields using refs
        const subjectInput = subjectInputRef.current;
        const contentEditor = contentEditorRef.current;

        // Insert into the last focused field
        if (lastFocusedField === "subject" && subjectInput) {
            const start = subjectInput.selectionStart || 0;
            const end = subjectInput.selectionEnd || 0;
            const newSubject =
                selectedTemplate?.email_subject.substring(0, start) +
                variable +
                selectedTemplate?.email_subject.substring(end);
            setSelectedTemplate((prev) => prev ? { ...prev, email_subject: newSubject } : null);

            // Set cursor position after the inserted variable
            setTimeout(() => {
                subjectInput.focus();
                subjectInput.setSelectionRange(
                    start + variable.length,
                    start + variable.length
                );
            }, 0);
        } else if (contentEditor) {
            // Insert text at current cursor position in the rich text editor
            contentEditor.insertText(variable);

            // Focus the editor after insertion
            setTimeout(() => {
                contentEditor.focus();
            }, 0);
        } else {
            // Fallback: append to end of content
            setSelectedTemplate((prev) => prev ? { ...prev, email_body: (prev.email_content + " " + variable) } : null);
        }
    };

    const handleCancel = () => {
        // Reset selected template to the last-saved version so any unsaved
        // edits are discarded from the preview.
        if (selectedTemplate) {
            const originalTemplate = templates.find(
                (template) => template.template_id === selectedTemplate.template_id
            );
            if (originalTemplate) {
                setSelectedTemplate(originalTemplate);
            }
        }
        // Switch back to view mode
        setIsViewMode(true);
    };

    const handleSaveSenderSettings = () => {
        updateCompanyMetadata({
            outgoing_email: {
                sender_name: senderName,
                default_reply_to: defaultReplyTo,
                is_job_specific_outgoing_email: allowJobBasedIdentity,
            },
        }).then((resp) => {
            if (resp.status === 200) {
                snackbar.showSuccess("Email settings saved.", "Default sender settings have been saved successfully.");
            }
        }).catch((error) => {
            snackbar.showError("Failed to update outgoing email settings. Please try again.");
            catchErrorLog(`failed to update outgoing email settings: ${error}`);
        });
    }

    const handleTogglePreference = (id: string) => {
        const preferencesCopy = preferences.map(pref => pref.id === id && !pref.alwaysEnabled ? { ...pref, enabled: !pref.enabled } : pref);
        setPreferences(preferencesCopy);
        handleUpdateNotificationPreferences(preferencesCopy)
    };

    const handleUpdateNotificationPreferences = (preferencesArr: NotificationPreference[]) => {
        updateCompanyNotificationPreferences({
            notify_candidate_activity: preferencesArr.find(p => p.id === "candidate-activity")?.enabled || false,
            notify_hiring_workflow_update: preferencesArr.find(p => p.id === "hiring-workflow")?.enabled || false,
            notify_team_workspace_update: preferencesArr.find(p => p.id === "team-workspace")?.enabled || false,
            notify_insight_reminders: preferencesArr.find(p => p.id === "insights-reminders")?.enabled || false,
        }).then((resp) => {
            if (resp.status === 200) {
                snackbar.showSuccess("Notification preferences updated successfully.");
            }
        }).catch((error) => {
            snackbar.showError("Failed to update notification preferences. Please try again.");
            catchErrorLog(`failed to update notification preferences: ${error}`);
        })
    }

    const hasSenderChanges = props.company &&
        ((props.company.company_metadata?.outgoing_email?.sender_name || "ourteam") !== senderName ||
            (props.company.company_metadata?.outgoing_email?.default_reply_to || "work@ourteam.ai") !== defaultReplyTo ||
            (props.company.company_metadata?.outgoing_email?.is_job_specific_outgoing_email || false) !== allowJobBasedIdentity);

    return (
        <>
            <div className="flex h-auto bg-white">
                {/* Left Panel - Title and Description */}
                <div
                    className="flex-[0_0_25%] p-6"
                    style={{ paddingTop: "32px", paddingBottom: "32px" }}
                >
                    <div className="flex items-start space-x-3">
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                Email Sender Settings
                            </h3>
                            <p className="text-sm text-gray-600">
                                Control how your company appears to candidates in all email communications.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Right Panel - Form Fields */}
                <div
                    className="flex-[0_0_75%] p-6"
                    style={{ paddingTop: "32px", paddingBottom: "32px" }}
                >
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Label
                                htmlFor="defaultSenderName"
                                className="text-sm font-medium text-gray-700"
                            >
                                Default Sender Name
                            </Label>
                            <p className="text-xs text-gray-500">
                                This name will appear as the sender in the candidate's inbox.
                            </p>
                            <Input
                                id="defaultSenderName"
                                value={senderName}
                                onChange={(e) => setSenderName(e.target.value)}
                                placeholder="e.g., ourteam"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label
                                htmlFor="defaultReplyToEmail"
                                className="text-sm font-medium text-gray-700"
                            >
                                Default Reply-To Email
                            </Label>
                            <p className="text-xs text-gray-500">
                                This address will receive all candidate responses to your
                                outgoing emails.
                            </p>
                            <Input
                                id="defaultReplyToEmail"
                                type="email"
                                value={defaultReplyTo}
                                onChange={(e) => setDefaultReplyTo(e.target.value)}
                                placeholder="e.g., work@ourteam.ai"
                            />
                        </div>

                        <div className="flex items-start space-x-3 pt-2">
                            <Checkbox
                                id="allowJobBasedIdentity"
                                checked={allowJobBasedIdentity}
                                onCheckedChange={(checked) =>
                                    setAllowJobBasedIdentity(checked as boolean)
                                }
                                className="mt-1.5 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600"
                            />
                            <div className="space-y-1">
                                <Label
                                    htmlFor="allowJobBasedIdentity"
                                    className="text-sm font-medium text-gray-700 cursor-pointer"
                                >
                                    Use Job-Specific Sender Details
                                </Label>
                                <p className="text-xs text-gray-500">
                                    When enabled, new jobs will use the job’s company name and job owner’s email by default. Existing jobs will not be affected.
                                </p>
                            </div>
                        </div>

                        <div className="flex justify-end pt-4">
                            <Button
                                onClick={handleSaveSenderSettings}
                                disabled={!hasSenderChanges}
                                className="bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Save Changes
                            </Button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Divider */}
            <div className="border-t border-gray-200" />

            <div className="flex h-auto bg-white">
                {/* Left Panel - Title and Description */}
                <div className="flex-[0_0_25%] p-6" style={{ paddingTop: "32px", paddingBottom: "32px" }}>
                    <div className="flex items-start space-x-3">
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                Email Notification Preferences
                            </h3>
                            <p className="text-sm text-gray-600">
                                Manage which types of emails you receive.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Right Panel - Form Fields */}
                <div className="flex-[0_0_75%] p-6" style={{ paddingTop: "32px", paddingBottom: "32px" }}>
                    <div className="space-y-6">
                        {preferences.map((preference) => (
                            <div key={preference.id} className="bg-white">
                                <div className="flex items-start space-x-3">
                                    <Checkbox
                                        id={preference.id}
                                        checked={preference.enabled}
                                        onCheckedChange={() => handleTogglePreference(preference.id)}
                                        disabled={preference.alwaysEnabled}
                                        className="mt-1 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600"
                                    />
                                    <div className="flex-1 space-y-2">
                                        <div className="flex items-center space-x-2">
                                            <Label
                                                htmlFor={preference.id}
                                                className={`text-sm font-semibold ${preference.alwaysEnabled
                                                    ? "text-gray-700"
                                                    : "text-gray-900 cursor-pointer"
                                                    }`}
                                            >
                                                {preference.title}
                                            </Label>
                                            {preference.alwaysEnabled && (
                                                <span className="text-xs bg-gray-200 text-gray-700 px-2 py-0.5 rounded">
                                                    Always enabled
                                                </span>
                                            )}
                                        </div>
                                        <div className="space-y-1">
                                            <p className="text-sm text-gray-700">
                                                {preference.description}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Divider */}
            <div className="border-t border-gray-200" />
            <div className="flex h-full max-w-full overflow-hidden" style={{ minHeight: isLaptopScreen ? "calc(100vh - 200px)" : "calc(100vh - 300px)" }}>
                {/* Left Panel - Template List */}
                <div
                    className={`${showVariables ? "flex-[0_0_22%]" : "flex-[0_0_25%]"} flex flex-col overflow-hidden min-w-0`}
                    style={{ height: panelHeight }}
                >
                    <div className={cn("flex items-start justify-between mb-6 p-6 pb-0 flex-shrink-0 transition-all duration-300")}>
                        <div className="space-y-2" style={{ paddingRight: "8px" }}>
                            <h2 className="text-lg font-semibold text-gray-900">
                                Email Templates
                            </h2>
                            <p className="text-sm text-gray-600">
                                Manage email templates for candidate communications.
                            </p>
                        </div>
                        {userInfo?.organization_role === "write" && (
                            <Button
                                onClick={handleCreateNewTemplate}
                                className="bg-blue-600 hover:bg-blue-700 text-white text-sm px-4 py-2 h-auto mt-1"
                            >
                                <PlusIcon className="h-4 w-4" />
                            </Button>
                        )}

                    </div>
                    <div className="flex-1 flex flex-col min-h-0 px-6">
                        {/* Category Dropdown */}
                        <div className="mb-4 flex-shrink-0">
                            <Label className="text-sm font-medium text-gray-700 mb-2 block">
                                Email Category
                            </Label>
                            <Select
                                value={selectedCategory}
                                onValueChange={handleCategoryChange}
                            >
                                <SelectTrigger className="w-full">
                                    <SelectValue placeholder="Select a category">
                                        {selectedCategory && (
                                            <div className="flex items-center gap-2">
                                                {createElement(getCategoryIcon(selectedCategory), {
                                                    className: `h-4 w-4 ${getCategoryColor(selectedCategory)}`,
                                                })}
                                                <span>
                                                    {
                                                        templateCategories[
                                                        selectedCategory as keyof typeof templateCategories
                                                        ]
                                                    }
                                                </span>
                                            </div>
                                        )}
                                    </SelectValue>
                                </SelectTrigger>
                                <SelectContent>
                                    {Object.entries(templateCategories).map(
                                        ([type, categoryName]) => {
                                            const IconComponent = getCategoryIcon(type);
                                            const colorClass = getCategoryColor(type);
                                            return (
                                                <SelectItem key={type} value={type}>
                                                    <div className="flex items-center gap-2">
                                                        <IconComponent className={`h-4 w-4 ${colorClass}`} />
                                                        <span>{categoryName}</span>
                                                    </div>
                                                </SelectItem>
                                            );
                                        }
                                    )}
                                </SelectContent>
                            </Select>
                        </div>
                        {/* Templates List with Scrolling */}
                        <div
                            className="flex-1 overflow-y-auto space-y-2 min-h-0 pb-6"
                            style={{
                                paddingTop: "2px",
                                paddingRight: "2px",
                                paddingBottom: "2px",
                                paddingLeft: "2px",
                                maxHeight: isCreatingNew ? "400px" : "none",
                            }}
                        >
                            {(templates || []).filter((template) => template.template_category === selectedCategory)
                                .map((template) => (
                                    <TemplateListItem
                                        key={template.template_id ?? template.template_name}
                                        template={template}
                                        isSelected={(selectedTemplate && selectedTemplate.template_id === template.template_id) ? true : false}
                                        onSelect={(template: EmailTemplate) => {
                                            setSelectedTemplate(template);
                                            setIsViewMode(true);
                                            setIsCreatingNew(false);
                                        }}
                                        onDelete={userInfo?.organization_role === "write" ? handleDeleteTemplate : undefined}
                                    />
                                ))}
                        </div>
                    </div>
                </div>

                {/* Middle Panel - Template Editor/Preview */}
                <div
                    className={`${showVariables ? "flex-[0_0_50%]" : "flex-[0_0_75%]"} flex flex-col min-w-0 ${showVariables ? "border-r border-gray-200" : ""}`}
                    style={{ height: panelHeight }}
                >
                    <div className="flex-1 flex flex-col min-h-0 p-6 overflow-y-auto">
                        {isCreatingNew ? (
                            <EmailTemplateCreateForm
                                selectedCategory={selectedCategory}
                                subjectRef={subjectInputRef}
                                contentRef={contentEditorRef}
                                showVariables={showVariables}
                                setLastFocusedField={setLastFocusedField}
                                onCancel={handleCancelNewTemplate}
                                onSave={handleSaveTemplate}
                                onShowVariable={() => setShowVariables(!showVariables)}
                            />
                        ) : selectedTemplate ? (
                            <EmailTemplateEditForm
                                showVariables={showVariables}
                                subjectRef={subjectInputRef}
                                contentRef={contentEditorRef}
                                isViewMode={isViewMode}
                                onChangeViewMode={setIsViewMode}
                                selectedTemplate={selectedTemplate}
                                onSave={handleSaveTemplate}
                                onCancel={handleCancel}
                                onDelete={handleDeleteTemplate}
                                setLastFocusedField={setLastFocusedField}
                                onShowVariable={() => setShowVariables(!showVariables)}
                                onTemplateChange={(key: string, value: string) => setSelectedTemplate((prev) => prev ? { ...prev, [key]: value } : null)}
                            />
                        ) : (
                            <div className="flex flex-col items-center justify-center h-64 text-center flex-shrink-0">
                                <p className="text-gray-500 mb-4">
                                    Select a template to view or edit, or create a new one.
                                </p>
                                <Button
                                    onClick={handleCreateNewTemplate}
                                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2"
                                >
                                    <PlusIcon className="h-4 w-4 mr-2" />
                                    Create New Template
                                </Button>
                            </div>
                        )}
                    </div>
                </div>

                {/* Right Panel - Variables (Conditional) */}
                {showVariables && (
                    <div
                        className="flex-[0_0_28%] flex-shrink-0 min-w-0"
                        style={{
                            height: "calc(100vh - 200px)",
                            maxHeight: "calc(100vh - 200px)",
                            minHeight: "400px",
                        }}
                    >
                        <EmailVariablesPanel
                            onClose={() => setShowVariables(false)}
                            onInsertVariable={handleInsertVariable}
                            isEditMode={isCreatingNew || !isViewMode}
                        />
                    </div>
                )}

                {/* Delete Confirmation Dialog */}
                <TemplateDeleteDialog
                    open={deleteDialogOpen}
                    onOpenChange={setDeleteDialogOpen}
                    template={templateToDelete}
                    onConfirmDelete={handleConfirmDelete}
                />
            </div>
        </>
    );
}
